// The manager's Home, in one request.
//
// Parity pass 2 batch 6. Home used to make four or five calls and assemble
// itself in the browser; on a phone on a site that difference is the page
// appearing at once rather than in pieces.
import { request } from './api';
import type { CalendarBooking } from './schedulingApi';

export type ReminderKind =
  | 'registration'
  | 'service'
  // Round 43: a draft quote's client deadline, near or missed.
  | 'quote'
  | 'record'
  // Round 41: a subcontractor's policy nearing its certificate's expiry.
  | 'insurance'
  | 'document'
  // Round 44: a week waiting for approval, or one not handed in.
  | 'timesheet'
  // Round 37, from the two optional dates on a person (migration 037).
  | 'birthday'
  | 'anniversary';

export interface Reminder {
  kind: ReminderKind;
  entityId: string;
  /** Negative when overdue. Only a sort key on reading-based service items. */
  days: number;
  /** Null when the item is driven by a meter rather than a date. */
  date: string | null;
  label: string;
  detail: string;
  overdue: boolean;
  link: string;
}

/** Today's board row. A maintenance booking has a supplier and no job. */
export type TodayBooking = Pick<
  CalendarBooking,
  'id' | 'kind' | 'startDate' | 'endDate' | 'startTime' | 'endTime' | 'inConflict'
> & {
  jobId: string | null;
  jobNumber: string | null;
  jobTitle: string | null;
  supplierName: string | null;
  resourceName: string;
};

export interface HomeData {
  today: string;
  todayBookings: TodayBooking[];
  reminders: { overdue: Reminder[]; today: Reminder[]; upcoming: Reminder[] };
  thresholds: { warnKm: number; warnHours: number; leadDays: number; personLeadDays: number };
  leadDays: number;
  flaggedCount: number;
  /** Round 45: Zanetto Tools' four job cards, from the jobs' money. */
  jobs: {
    inProgress: number;
    notStarted: number;
    notStartedCents: number;
    needsInvoicing: number;
    workInProgressCents: number;
  };
  activeClients: number;
  /** ZT's Active Jobs card: the six newest open jobs, with their values. */
  activeJobs: {
    id: string;
    number: string;
    title: string;
    status: string;
    clientName: string | null;
    valueCents: number;
  }[];
  recent: {
    posts: {
      id: string;
      body: string;
      createdAt: string;
      authorName: string | null;
      edited: boolean;
      photos: number;
      documents: number;
    }[];
    jobs: { id: string; number: string; title: string; createdAt: string }[];
    submissions: {
      id: string;
      templateName: string;
      about: string;
      byName: string | null;
      at: string | null;
    }[];
  };
  checklist: {
    resources: number;
    jobs: number;
    bookings: number;
    posts: number;
    meters: number;
    publishedForms: number;
    invited: boolean;
  };
  /** Round 43. Always present since round 45 (quotes are always on). */
  quotes: {
    sent: number;
    draft: number;
    wonThisFinancialYear: number;
    wonThisFinancialYearCents: number;
    recent: {
      id: string;
      number: string;
      title: string;
      status: string;
      clientName: string;
      totalCents: number;
    }[];
  } | null;
  /** Round 44. Null for a company with timesheets switched off. */
  timesheets: {
    thisWeekStart: string;
    lastWeekStart: string;
    awaitingApproval: number;
    returned: number;
    notHandedInLastWeek: number;
  } | null;
}

export interface NotificationsFeed {
  today: string;
  groups: { overdue: Reminder[]; today: Reminder[]; upcoming: Reminder[] };
  /** The general window (7 by default) and the people-records one (60). */
  leadDays: number;
  personLeadDays: number;
  /**
   * Per-kind totals for the filter chips, plus `total`, `overdue` and
   * `badge` — the last counting overdue AND TODAY only. Computed on the
   * server so the badge and the page can never disagree.
   */
  counts: Record<string, number>;
}

/**
 * The system-generated feed (round 37). Manager+; the same engine Home's
 * summary is built from, so the two cannot contradict each other.
 */
export const notificationsFeed = {
  load: () => request<NotificationsFeed>('/notifications'),
};

export const home = {
  load: () => request<HomeData>('/home'),
  /** Admin only. The service warn bands and the reminder lead days. */
  setThresholds: (body: {
    serviceWarnKm: number;
    serviceWarnHours: number;
    reminderLeadDays: number;
    personExpiryLeadDays: number;
  }) =>
    request<{ thresholds: { warnKm: number; warnHours: number; leadDays: number; personLeadDays: number } }>(
      '/resources/thresholds',
      { method: 'PATCH', body }
    ),
};
